/**
 * MODULE: home/LiveIntelligenceMapSection.jsx
 * PURPOSE: The homepage's "Live intelligence map" — 4-level drill-down
 *          (Continent → Region → Country → State) over WorldMap.jsx, plus the
 *          adaptive side panel (state intel / country list / live feed).
 *          Extracted from monolithic pages/Home.jsx (Phase 4 split).
 * PUBLIC API: <WorldPulseSection />
 * DEPENDS ON: components/dnh/WorldMap.jsx, components/dnh/continents.js,
 *   components/dnh/countryCentroids.js, GET /api/map/live,
 *   GET /api/map/state-intelligence/:iso2, home/LiveIntelligenceSection.LiveFeedPanel
 * CALLED BY: pages/Home.jsx
 * FAILURE MODES: see GET /api/system/health-deep for map-data related checks.
 * LAST REVIEWED: 2026-07-17
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import WorldMap, { ZOOM_CONFIGS, COUNTRY_ZOOM_CONFIGS } from "../WorldMap";
import { CONTINENTS, REGIONS, ISO_TO_CONTINENT, ISO_TO_REGION } from "../continents";
import { centroidForIso2 } from "../countryCentroids";
import { SectionHeading } from "../atoms";
import { getMapLive, apiFetch } from "../../../utils/api";
import { usePlatformStats } from "./PlatformStatsContext";
import { timeAgo } from "./homeUtils";
import { LiveFeedPanel } from "./LiveIntelligenceSection";

const MAP_FILTERS = [
  { slug: "all", label: "All" },
  { slug: "politics", label: "Politics" },
  { slug: "business-economy", label: "Economy" },
  { slug: "technology", label: "Technology" },
  { slug: "health-lifestyle", label: "Health" },
  { slug: "science", label: "Science" },
  { slug: "climate-energy", label: "Climate" },
];

export const WorldPulseSection = () => {
  const stats      = usePlatformStats();
  const navigate   = useNavigate();
  const [activeId,    setActiveId]    = useState(null);
  const [mapData,     setMapData]     = useState(null);
  const [catFilter,   setCatFilter]   = useState("all");

  // ── Drill-down navigation state ──────────────────────────────────────
  // drillStack: [{level: "continent"|"region"|"country", id, label}]
  const [drillStack,       setDrillStack]       = useState([]);
  const [activeDropdown,   setActiveDropdown]   = useState(null); // "continent"|"region"|"country"
  const [hoveredNavButton, setHoveredNavButton] = useState(null); // button hover → preview
  const [hoveredDropItem,  setHoveredDropItem]  = useState(null); // dropdown item hover

  // Outside-click refs for each dropdown
  const contDropRef    = useRef(null);
  const regionDropRef  = useRef(null);
  const countryDropRef = useRef(null);
  const stateDropRef   = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const refs = [contDropRef, regionDropRef, countryDropRef, stateDropRef];
      if (refs.every((r) => !r.current || !r.current.contains(e.target))) {
        setActiveDropdown(null);
        setHoveredDropItem(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Derived drill values
  const drillContinent = drillStack.find((d) => d.level === "continent");
  const drillRegion    = drillStack.find((d) => d.level === "region");
  const drillCountry   = drillStack.find((d) => d.level === "country");

  // ── Country story feed — fetched when a country is drilled in ──
  const [countryFeed,  setCountryFeed]  = useState(null);
  const [feedLoading,  setFeedLoading]  = useState(false);

  useEffect(() => {
    if (!drillCountry) { setCountryFeed(null); return; }
    setFeedLoading(true);
    setCountryFeed(null);
    apiFetch(`/api/country-feed/${encodeURIComponent(drillCountry.id)}?limit=4`)
      .then(d => setCountryFeed(d?.articles || []))
      .catch(() => setCountryFeed([]))
      .finally(() => setFeedLoading(false));
  }, [drillCountry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const drillDown = (item) => {
    const ORDER = ["continent", "region", "country"];
    const idx   = ORDER.indexOf(item.level);
    setDrillStack((prev) => {
      const trimmed = prev.filter((d) => ORDER.indexOf(d.level) < idx);
      return [...trimmed, item];
    });
    setActiveDropdown(null);
    setHoveredDropItem(null);
    setHoveredNavButton(null);
  };

  // Click on a country polygon on the map → auto-drill Continent > Region > Country
  const drillToCountry = (iso2, slug, name) => {
    const upper = (iso2 || "").toUpperCase();
    const continentId = ISO_TO_CONTINENT[upper];
    const regionId    = ISO_TO_REGION[upper];
    const stack = [];
    if (continentId && CONTINENTS[continentId]) {
      stack.push({ level: "continent", id: continentId, label: CONTINENTS[continentId].label });
    }
    if (regionId && REGIONS[regionId]) {
      stack.push({ level: "region", id: regionId, label: REGIONS[regionId].label });
    }
    if (slug && name) {
      stack.push({ level: "country", id: slug, label: name, iso2: upper || null });
    }
    if (stack.length) {
      setDrillStack(stack);
      setActiveDropdown(null);
      setHoveredDropItem(null);
      setHoveredNavButton(null);
    }
  };

  const drillBack = (toLevel) => {
    const ORDER = ["continent", "region", "country"];
    const idx   = ORDER.indexOf(toLevel);
    setDrillStack((prev) => prev.filter((d) => ORDER.indexOf(d.level) < idx));
    setActiveDropdown(null);
    setHoveredDropItem(null);
  };

  // ── Live map data ─────────────────────────────────────────────────────
  useEffect(() => {
    getMapLive().then(setMapData).catch(() => {});
    const id = setInterval(
      () => getMapLive().then(setMapData).catch(() => {}),
      5 * 60 * 1000
    );
    return () => clearInterval(id);
  }, []);

  const allPoints = useMemo(() => {
    return (mapData?.points || [])
      .map((p) => {
        const coords = centroidForIso2(p.iso2);
        if (!coords) return null;
        return {
          id:          p.country_slug,
          slug:        p.country_slug,
          iso2:        p.iso2,
          city:        p.country_name,
          country:     "",
          lon:         coords[0],
          lat:         coords[1],
          confidence:  p.confidence || "Developing",
          sources:     `${p.count} signal${p.count === 1 ? "" : "s"}`,
          category:    p.category_slug || "",
          count:       p.count,
          featured:    true,
          timeAgo:     timeAgo(p.published_at),
          headline:    p.headline,
          story_id:    p.story_id    || null,
          source_name: p.source_name || "",
        };
      })
      .filter(Boolean);
  }, [mapData]);

  // Category-filtered pulse points
  const pulsePoints = useMemo(
    () => catFilter === "all" ? allPoints : allPoints.filter((p) => p.category === catFilter),
    [allPoints, catFilter]
  );

  // ── Dropdown item lists ───────────────────────────────────────────────
  const continentItems = useMemo(() => Object.values(CONTINENTS), []);
  const regionItems    = useMemo(() => {
    const all = Object.values(REGIONS);
    return drillContinent ? all.filter((r) => r.continent === drillContinent.id) : all;
  }, [drillContinent]);
  const countryItems   = useMemo(() => {
    let pts = pulsePoints;
    if (drillRegion) {
      pts = pts.filter((p) => ISO_TO_REGION[(p.iso2 || "").toUpperCase()] === drillRegion.id);
    } else if (drillContinent) {
      pts = pts.filter((p) => ISO_TO_CONTINENT[(p.iso2 || "").toUpperCase()] === drillContinent.id);
    }
    return [...pts].sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [pulsePoints, drillContinent, drillRegion]);

  // ── Map derived props ─────────────────────────────────────────────────
  const focusScope = useMemo(() => {
    if (drillRegion)    return { regionId: drillRegion.id };
    if (drillContinent) return { continentId: drillContinent.id };
    return null;
  }, [drillContinent, drillRegion]);

  const scopedDots = useMemo(() => {
    if (!focusScope) return pulsePoints;
    return pulsePoints.filter((p) => {
      const upper = (p.iso2 || "").toUpperCase();
      if (focusScope.regionId)    return ISO_TO_REGION[upper]    === focusScope.regionId;
      if (focusScope.continentId) return ISO_TO_CONTINENT[upper] === focusScope.continentId;
      return true;
    });
  }, [pulsePoints, focusScope]);

  const mapZoom = useMemo(() => {
    if (drillCountry) {
      const dot = allPoints.find((p) => p.slug === drillCountry.id);
      if (dot) {
        const iso2 = (dot.iso2 || "").toUpperCase();
        // Use country-specific zoom if available (shows state boundaries well)
        if (COUNTRY_ZOOM_CONFIGS[iso2]) return COUNTRY_ZOOM_CONFIGS[iso2];
        return { scale: 1100, center: [dot.lon, dot.lat] };
      }
    }
    if (drillRegion    && ZOOM_CONFIGS[drillRegion.id])    return ZOOM_CONFIGS[drillRegion.id];
    if (drillContinent && ZOOM_CONFIGS[drillContinent.id]) return ZOOM_CONFIGS[drillContinent.id];
    return null;
  }, [drillContinent, drillRegion, drillCountry, allPoints]);

  // ISO2 of the selected country (used for state-level rendering in WorldMap)
  const selectedCountryIso2 = useMemo(() => {
    if (!drillCountry) return null;
    // Use iso2 stored directly on drillStack item (set by drillToCountry)
    if (drillCountry.iso2) return drillCountry.iso2.toUpperCase();
    // Fallback: look up from live dots (covers dropdown-drilled countries)
    const dot = allPoints.find((p) => p.slug === drillCountry.id);
    return dot ? (dot.iso2 || "").toUpperCase() || null : null;
  }, [drillCountry, allPoints]);

  // ── State-level intelligence ──────────────────────────────────────────────
  const [stateIntel, setStateIntel] = useState(null);   // null = not loaded
  const [stateLoading, setStateLoading] = useState(false);

  useEffect(() => {
    if (!selectedCountryIso2) { setStateIntel(null); return; }
    let cancelled = false;
    setStateLoading(true);
    setStateIntel(null);
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_BASE}/api/map/state-intelligence/${selectedCountryIso2}`)
      .then((r) => r.json())
      .then((data) => { if (!cancelled) { setStateIntel(data.states || []); setStateLoading(false); } })
      .catch(() => { if (!cancelled) { setStateIntel([]); setStateLoading(false); } });
    return () => { cancelled = true; };
  }, [selectedCountryIso2]);

  // Map<lowercase_name → {signal_level, headline, type}> for state dot rendering in WorldMap
  const stateIntelMap = useMemo(() => {
    if (!stateIntel || stateIntel.length === 0) return null;
    const m = new Map();
    stateIntel.forEach((s) => m.set(s.name.toLowerCase(), {
      signal_level: s.signal_level,
      headline:     s.headline,
      type:         s.type,
    }));
    return m;
  }, [stateIntel]);

  // State dropdown items (4th drill level — terminal selection, navigates to /state/:iso2/:slug)
  const stateItems = useMemo(() => {
    if (!stateIntel || stateIntel.length === 0) return [];
    return stateIntel.map((s) => ({
      id:    s.slug,
      label: `${s.name} · ${s.code}`,
      signal_level: s.signal_level,
    }));
  }, [stateIntel]);

  const goToState = (iso2, stateSlug) => navigate(`/state/${iso2}/${stateSlug}`);

  // When state intel is loaded, suppress country-level dots (state dots render in WorldMap)
  const mapDots = useMemo(() => {
    if (drillCountry && stateIntelMap) return [];
    return scopedDots;
  }, [drillCountry, stateIntelMap, scopedDots]);

  // mode for background tints: world=continents, inside-continent=regions, deeper=continents
  const mapDisplayMode = drillContinent && !drillRegion ? "regions" : "continents";

  // previewAll: button hover (if no dropdown item is being hovered)
  const previewAll = hoveredDropItem ? null : (
    hoveredNavButton === "continent" ? "continents" :
    hoveredNavButton === "region"    ? "regions"    :
    hoveredNavButton === "country"   ? "countries"  : null
  );

  // hoveredGroupId: dropdown item hover (continent/region level)
  const hoveredGroupId = (hoveredDropItem && hoveredDropItem.level !== "country") ? hoveredDropItem.id : null;
  // hoveredCountryIso2: country dropdown item hover → highlight individual polygon
  const hoveredCountryIso2 = hoveredDropItem?.level === "country" ? (hoveredDropItem.iso2 || null) : null;

  const openCountry = (p) => p?.slug && navigate(`/country/${p.slug}`);
  const featured    = scopedDots;

  // ── Signal counts per group (for dropdown badges) ────────────────────
  const continentCounts = useMemo(() => {
    const m = {};
    for (const p of pulsePoints) {
      const c = ISO_TO_CONTINENT[(p.iso2 || "").toUpperCase()];
      if (c) m[c] = (m[c] || 0) + (p.count || 1);
    }
    return m;
  }, [pulsePoints]);
  const regionCounts = useMemo(() => {
    const m = {};
    for (const p of pulsePoints) {
      const r = ISO_TO_REGION[(p.iso2 || "").toUpperCase()];
      if (r) m[r] = (m[r] || 0) + (p.count || 1);
    }
    return m;
  }, [pulsePoints]);

  // ── Drill Dropdown Button component (inline) ──────────────────────────
  const DrillDropdown = ({ level, label, items, counts = {}, currentItem, dropRef, isEnabled = true, isCountries = false, onSelect = null, emptyLabel = "No signals in this scope" }) => {
    const isOpen    = activeDropdown === level;
    const hasValue  = !!currentItem;

    const buttonClasses = `
      inline-flex items-center gap-1.5 h-7 px-3 rounded-full border
      bg-[var(--paper)]/90 backdrop-blur-sm font-mono text-[10px] tracking-[0.1em] uppercase
      transition-all duration-150
      ${hasValue
        ? "border-[#36BFF0] text-[#1a6a9e] bg-[#CFEAFB]/90"
        : isEnabled
        ? "border-[var(--hairline)] text-[var(--ink)] hover:border-[#36BFF0] hover:text-[#1a6a9e]"
        : "border-[var(--hairline)] text-[var(--muted)] opacity-50 cursor-not-allowed"}
    `.trim().replace(/\s+/g, " ");

    return (
      <div ref={dropRef} className="relative" data-testid={`drill-dropdown-${level}`}>
        <button
          type="button"
          disabled={!isEnabled}
          onMouseEnter={() => isEnabled && setHoveredNavButton(level)}
          onMouseLeave={() => { if (activeDropdown !== level) setHoveredNavButton(null); }}
          onClick={() => {
            if (!isEnabled) return;
            setActiveDropdown((v) => (v === level ? null : level));
            setHoveredDropItem(null);
          }}
          className={buttonClasses}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          data-testid={`drill-btn-${level}`}
        >
          {hasValue ? (
            <>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#36BFF0]" />
              <span className="max-w-[90px] truncate">{currentItem.label}</span>
              <span
                className="ml-0.5 opacity-60 hover:opacity-100 text-[11px] leading-none font-bold"
                onClick={(e) => { e.stopPropagation(); drillBack(level); }}
                role="button"
                aria-label={`Clear ${level}`}
              >
                ×
              </span>
            </>
          ) : (
            <>
              {label}
              <ChevronDown size={10} strokeWidth={2}
                className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </>
          )}
        </button>

        {isOpen && (
          <div
            role="listbox"
            data-testid={`drill-panel-${level}`}
            className="absolute top-full right-0 mt-1.5 w-[210px] rounded-[12px] border border-[var(--hairline)] bg-[var(--paper)] shadow-xl py-1 z-40 max-h-[300px] overflow-y-auto"
            onMouseLeave={() => setHoveredDropItem(null)}
          >
            {items.length === 0 && (
              <div className="px-4 py-3 font-mono text-[10.5px] text-[var(--muted)] text-center">
                {emptyLabel}
              </div>
            )}
            {items.map((g) => {
              const isHov = hoveredDropItem?.id === g.id;
              const cnt   = isCountries ? (g.count || 0) : (counts[g.id] ?? 0);
              return (
                <button
                  key={g.id || g.slug}
                  type="button"
                  role="option"
                  aria-selected={isHov}
                  onMouseEnter={() => setHoveredDropItem({ id: g.id || g.slug, level, label: g.label || g.city, ...(isCountries && g.iso2 ? { iso2: g.iso2 } : {}) })}
                  onClick={() => {
                    if (onSelect) {
                      onSelect(g);
                      return;
                    }
                    if (isCountries) {
                      // Country dropdown: zoom into the country on the map (no navigation away)
                      const dot = allPoints.find((p) => p.slug === (g.id || g.slug));
                      if (dot) {
                        drillDown({ level: "country", id: g.id || g.slug, label: g.label || g.city, iso2: dot.iso2 });
                      }
                    } else {
                      drillDown({ level, id: g.id, label: g.label });
                    }
                  }}
                  data-testid={`drill-item-${(g.id || g.slug || "").toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                  className={`w-full flex items-center justify-between gap-2 px-3.5 py-2 text-left transition-colors ${
                    isHov ? "bg-[#CFEAFB] text-[#1a6a9e]" : "hover:bg-[var(--surface-2)] text-[var(--ink)]"
                  }`}
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: isHov ? "#36BFF0" : "#BFB3A0", transition: "background 150ms" }} />
                    <span className={`font-sans text-[12.5px] leading-none ${isHov ? "font-medium" : ""} truncate`}>
                      {g.label || g.city}
                    </span>
                  </span>
                  <span className="font-mono text-[10px] text-[var(--muted)] shrink-0">{cnt || "—"}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <section
      className="py-14 sm:py-18 lg:py-24 border-t border-[var(--hairline)]"
      data-testid="world-pulse-section"
      id="world-pulse-section"
    >
      <div className="mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-10">
        <SectionHeading
          label="World Pulse · Live"
          title="Live intelligence map"
          subtitle="Drill into continents, regions, and countries. Click any polygon to zoom in — state-level intelligence loads automatically."
          action={
            <div className="hidden md:flex items-center gap-5 font-mono text-[11px] text-[var(--muted)]">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[var(--crimson)]" />High
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[var(--ink)]" />Moderate
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full border border-[var(--muted)]" />Developing
              </span>
              <span className="text-[var(--hairline-strong)]">|</span>
              <span className="inline-flex items-center gap-2">
                <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-[var(--crimson)]">
                  <span className="absolute inset-0 rounded-full bg-[var(--crimson)] opacity-60 animate-ping" />
                </span>
                <span data-testid="world-pulse-updated-timestamp">
                  Live · {pulsePoints.length} countries reporting
                </span>
              </span>
            </div>
          }
        />

        <div className="grid grid-cols-12 gap-5 lg:gap-6 lg:items-stretch">
          {/* Map canvas */}
          <div
            className="col-span-12 lg:col-span-8 order-2 lg:order-1 lg:h-[560px] rounded-[18px] border border-[var(--hairline)] bg-[var(--paper)] p-[10px] relative overflow-hidden flex flex-col"
            data-testid="world-pulse-map"
          >
            {/* Overlay row */}
            <div className="absolute top-[10px] left-[10px] right-[10px] z-30 flex items-start justify-between gap-3 pointer-events-none">

              {/* Left: streaming + breadcrumb */}
              <div className="pointer-events-auto inline-flex flex-col gap-1">
                <div className="inline-flex items-center gap-2 font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--muted)]">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--crimson)]" />
                  Streaming · {stats?.sources_count ?? "…"} sources
                </div>
                {drillStack.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setDrillStack([]); setActiveDropdown(null); }}
                    data-testid="map-back-to-world"
                    className="inline-flex items-center gap-1.5 font-mono text-[9.5px] tracking-[0.14em] uppercase text-[#1a6a9e] hover:text-[var(--crimson)] transition-colors"
                  >
                    ← Back to world map
                  </button>
                )}
                {/* Breadcrumb trail */}
                {drillStack.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    {drillStack.map((d, i) => (
                      <span key={d.level} className="flex items-center gap-1">
                        {i > 0 && <span className="font-mono text-[9px] text-[var(--muted)]">›</span>}
                        <button
                          type="button"
                          onClick={() => drillBack(d.level)}
                          className="font-mono text-[9px] tracking-[0.08em] uppercase bg-[#CFEAFB] text-[#1a6a9e] px-1.5 py-0.5 rounded-full hover:bg-[#B5E0F8] transition-colors"
                        >
                          {d.label}
                        </button>
                      </span>
                    ))}
                    {drillCountry && (
                      <button
                        type="button"
                        onClick={() => navigate(`/country/${drillCountry.id}`)}
                        data-testid="map-view-country-page-link"
                        className="font-mono text-[9px] tracking-[0.1em] uppercase text-[var(--muted)] hover:text-[var(--crimson)] transition-colors underline decoration-dotted"
                      >
                        View {drillCountry.label} page →
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Right: 4 drill dropdown buttons */}
              <div className="pointer-events-auto flex items-center gap-1.5" data-testid="map-drill-nav">
                <DrillDropdown
                  level="continent"
                  label="Continent"
                  items={continentItems}
                  counts={continentCounts}
                  currentItem={drillContinent}
                  dropRef={contDropRef}
                  isEnabled={true}
                />
                <DrillDropdown
                  level="region"
                  label="Region"
                  items={regionItems}
                  counts={regionCounts}
                  currentItem={drillRegion}
                  dropRef={regionDropRef}
                  isEnabled={!!drillContinent}
                />
                <DrillDropdown
                  level="country"
                  label="Country"
                  items={countryItems}
                  counts={{}}
                  currentItem={drillCountry}
                  dropRef={countryDropRef}
                  isEnabled={!!(drillContinent || drillRegion)}
                  isCountries={true}
                />
                <DrillDropdown
                  level="state"
                  label="State"
                  items={stateItems}
                  counts={{}}
                  currentItem={null}
                  dropRef={stateDropRef}
                  isEnabled={!!drillCountry && stateItems.length > 0}
                  emptyLabel={stateLoading ? "Loading states…" : "No state data yet"}
                  onSelect={(s) => { setActiveDropdown(null); goToState(selectedCountryIso2, s.id); }}
                />
              </div>
            </div>

            {/* Map */}
            <div className="flex-1 w-full min-h-0 pt-9 flex flex-col">
              <WorldMap
                dots={mapDots}
                activeId={activeId}
                mode={mapDisplayMode}
                mapZoom={mapZoom}
                previewAll={previewAll}
                hoveredGroupId={hoveredGroupId}
                hoveredCountryIso2={hoveredCountryIso2}
                selectedCountryIso2={selectedCountryIso2}
                stateIntelMap={stateIntelMap}
                focusScope={focusScope}
                onDotEnter={(p) => setActiveId(p.id)}
                onDotLeave={() => setActiveId(null)}
                onDotClick={openCountry}
                onStateClick={(stateName) => {
                  if (!selectedCountryIso2) return;
                  const norm = value => value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
                  const matches = (stateIntel || []).filter(s => [s.name, ...(s.aliases || [])].some(value => norm(value) === norm(stateName)));
                  if (matches.length === 1) goToState(selectedCountryIso2, matches[0].slug);
                  else navigate(`/state/${selectedCountryIso2}`);
                }}
                onCountryClick={(info) => {
                  // Drill into the country (stay on map) instead of navigating away
                  const dot = allPoints.find(
                    (p) => p.iso2 && info.iso2 && p.iso2.toUpperCase() === info.iso2.toUpperCase()
                  );
                  if (dot?.iso2 && dot?.slug) {
                    drillToCountry(dot.iso2, dot.slug, dot.city || info.name || dot.slug);
                  } else if (info.iso2 && info.name) {
                    const slug = info.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                    drillToCountry(info.iso2, slug, info.name);
                  }
                }}
              />
            </div>

            {/* Country level: state map badge */}
            {drillCountry && (
              <div className="absolute bottom-[52px] left-1/2 -translate-x-1/2 z-20 bg-[var(--ink)]/80 backdrop-blur-sm rounded-full px-4 py-1.5 pointer-events-none">
                <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-[var(--paper)]">
                  {selectedCountryIso2 && ["IN","US","RU","CN","BR","CA","ID","AU","ZA"].includes(selectedCountryIso2)
                    ? `${drillCountry.label} · State / province boundaries`
                    : `${drillCountry.label} · State-level intelligence arriving soon`
                  }
                </span>
              </div>
            )}

            {/* Category filter strip */}
            <div className="hidden md:flex items-center gap-2 mt-2 pt-2.5 border-t border-[var(--hairline)] flex-wrap shrink-0">
              <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)] mr-1">Filter</span>
              {MAP_FILTERS.map((f) => {
                const count  = f.slug === "all" ? allPoints.length : allPoints.filter((p) => p.category === f.slug).length;
                const active = catFilter === f.slug;
                return (
                  <button key={f.slug} type="button" onClick={() => setCatFilter(f.slug)}
                    className={`dnh-chip inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full border text-[11.5px] font-medium transition-colors ${
                      active ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]"
                             : "border-[var(--hairline)] text-[var(--ink-2)] hover:border-[var(--hairline-strong)]"
                    }`} data-testid={`map-filter-${f.slug}`}>
                    {f.label}
                    <span className={`font-mono text-[9.5px] ${active ? "text-[var(--paper)]/70" : "text-[var(--muted)]"}`}>
                      {count || "—"}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Mobile legend */}
            <div className="md:hidden mt-4 pt-4 border-t border-[var(--hairline)] flex items-center justify-between gap-4 flex-wrap font-mono text-[11px] text-[var(--muted)]">
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full bg-[var(--crimson)]" />High</span>
                <span className="inline-flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full bg-[var(--ink)]" />Moderate</span>
                <span className="inline-flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full border border-[var(--muted)]" />Developing</span>
              </div>
              <span>{pulsePoints.length} countries · {mapData?.signals_48h ?? 0} live signals</span>
            </div>
          </div>

          {/* Side panel — adapts to drill level */}
          <aside
            className="col-span-12 lg:col-span-4 order-1 lg:order-2 max-h-[300px] lg:max-h-none lg:h-[560px] flex flex-col rounded-[18px] border border-[var(--hairline)] bg-[var(--paper)] p-4 lg:p-5 overflow-hidden"
            data-testid="world-pulse-sidepanel"
          >
            {drillCountry ? (
              /* Country level: per-state/province intelligence */
              <>
                <div className="flex items-center justify-between mb-3 shrink-0">
                  <div className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--crimson)]" />
                    <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
                      {drillCountry.label} · State Intel
                    </span>
                  </div>
                  {stateIntel && !stateLoading && (
                    <span className="font-mono text-[9.5px] text-[var(--muted)]">
                      {stateIntel.length} regions
                    </span>
                  )}
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto" data-testid="state-intel-panel">
                  {/* ── Recent stories from this country ── */}
                  {(feedLoading || (countryFeed && countryFeed.length > 0)) && (
                    <div className="mb-3 pb-3 border-b border-[var(--hairline)]" data-testid="country-story-stream">
                      <p className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-[var(--muted)] mb-2 px-0.5">
                        Recent stories
                      </p>
                      {feedLoading && !countryFeed ? (
                        <div className="flex flex-col gap-1">
                          {[0, 1, 2].map(i => (
                            <div key={i} className="animate-pulse px-2 py-1.5">
                              <div className="h-3 bg-[var(--surface-2)] rounded w-4/5 mb-1.5" />
                              <div className="h-2 bg-[var(--surface-2)] rounded w-2/5" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          {(countryFeed || []).slice(0, 4).map((a, i) => (
                            <button
                              key={a.id || i}
                              type="button"
                              onClick={() => a.id && navigate(`/briefs/${a.id}`)}
                              className="w-full text-left rounded-[6px] px-2 py-1.5 hover:bg-[var(--surface-2)] transition-colors group"
                              data-testid={`country-story-${i}`}
                            >
                              <p className="text-[11.5px] font-medium text-[var(--ink)] line-clamp-2 leading-snug group-hover:text-[var(--crimson)] transition-colors">
                                {a.headline}
                              </p>
                              {a.source_name && (
                                <p className="text-[10px] text-[var(--muted)] mt-0.5 font-mono truncate">
                                  {a.source_name}
                                </p>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── State / province intelligence ── */}
                  {stateLoading ? (
                    /* Loading state */
                    <div className="flex flex-col gap-2.5 pt-1">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="animate-pulse rounded-[8px] border border-[var(--hairline)] p-3">
                          <div className="h-3 bg-[var(--surface-2)] rounded w-3/5 mb-2" />
                          <div className="h-2.5 bg-[var(--surface-2)] rounded w-1/4 mb-2" />
                          <div className="h-2 bg-[var(--surface-2)] rounded w-full mb-1" />
                          <div className="h-2 bg-[var(--surface-2)] rounded w-4/5" />
                        </div>
                      ))}
                    </div>
                  ) : stateIntel && stateIntel.length > 0 ? (
                    stateIntel.map((s, i) => {
                      const stateSlugVal = s.slug;
                      return (
                      <div
                        key={`${s.name}-${i}`}
                        className="rounded-[8px] border border-[var(--hairline)] hover:border-[var(--ink-2)] hover:bg-[var(--surface-2)] transition-all mb-2 p-3 cursor-default"
                        data-testid={`state-intel-card-${i}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <span className="font-sans text-[12.5px] font-semibold text-[var(--ink)] leading-tight">
                            {s.name}
                          </span>
                          <span className={`font-mono text-[8.5px] tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-full shrink-0 ${
                            s.signal_level === "High"
                              ? "bg-[var(--crimson)] text-[var(--paper)]"
                              : s.signal_level === "Moderate"
                              ? "bg-[var(--ink)] text-[var(--paper)]"
                              : "bg-[var(--surface-2)] text-[var(--muted)]"
                          }`}>
                            {s.signal_level}
                          </span>
                        </div>
                        <div className="font-mono text-[9px] text-[var(--muted)] uppercase tracking-[0.1em] mb-1.5">
                          {s.type}
                        </div>
                        <div className="font-sans text-[12px] text-[var(--ink-2)] leading-[1.45] mb-1.5">
                          {s.headline}
                        </div>
                        <div className="font-sans text-[11px] text-[var(--muted)] leading-[1.5] mb-2">
                          {s.summary}
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`/state/${selectedCountryIso2}/${stateSlugVal}`)}
                          className="font-mono text-[9px] tracking-[0.1em] uppercase text-[var(--ink-2)] hover:text-[var(--crimson)] transition-colors"
                          data-testid={`state-brief-link-${i}`}
                        >
                          Full state brief →
                        </button>
                      </div>
                      );
                    })
                  ) : stateIntel !== null ? (
                    <div className="flex items-center justify-center h-full font-mono text-[11px] text-[var(--muted)] text-center px-4">
                      No state intelligence available yet
                    </div>
                  ) : null}
                </div>

                <div className="mt-3 pt-3 border-t border-[var(--hairline)] shrink-0 flex items-center justify-between">
                  <span className="font-mono text-[9.5px] text-[var(--muted)]">
                    AI-generated · cached 6h
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate(`/country/${drillCountry.id}`)}
                    className="font-mono text-[9.5px] text-[var(--ink-2)] hover:text-[var(--crimson)] transition-colors"
                  >
                    Full country brief →
                  </button>
                </div>
              </>
            ) : drillContinent || drillRegion ? (
              /* Continent or Region level: filtered country list */
              <>
                <div className="flex items-center gap-2 mb-3 shrink-0">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--crimson)]" />
                  <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
                    {drillRegion ? drillRegion.label : drillContinent?.label} · {scopedDots.length} countries
                  </span>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto" data-testid="country-list-panel">
                  {countryItems.map((p) => (
                    <button key={p.id} type="button"
                      onClick={() => drillDown({ level: "country", id: p.slug, label: p.city, iso2: p.iso2 })}
                      onMouseEnter={() => setActiveId(p.id)}
                      onMouseLeave={() => setActiveId(null)}
                      data-testid={`country-item-${p.slug}`}
                      className={`w-full flex items-center justify-between gap-2 px-2 py-2.5 rounded-[6px] text-left transition-colors ${
                        activeId === p.id ? "bg-[var(--surface-2)]" : "hover:bg-[var(--surface-2)]"
                      }`}>
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className="font-mono text-[9px] tracking-[0.08em] uppercase text-[var(--muted)] shrink-0 w-6 text-center">{p.iso2}</span>
                        <span className="font-sans text-[13px] text-[var(--ink)] leading-none truncate">{p.city}</span>
                      </span>
                      <span className={`font-mono text-[10px] shrink-0 px-1.5 py-0.5 rounded-full ${
                        p.confidence === "High" ? "bg-[var(--crimson)] text-[var(--paper)]" : "bg-[var(--surface-2)] text-[var(--muted)]"
                      }`}>{p.count}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--hairline)] shrink-0 font-mono text-[10px] text-[var(--muted)]">
                  Click to drill into country
                </div>
              </>
            ) : (
              /* World level: auto-scrolling live feed */
              <>
                <div className="flex items-center justify-between mb-3 shrink-0">
                  <div className="inline-flex items-center gap-2 font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--crimson)]" />
                    Live feed
                    <span className="text-[var(--hairline-strong)]">·</span>
                    <span>{featured.length}</span>
                  </div>
                  <Link to="/briefs" className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ink-2)] hover:text-[var(--crimson)]" data-testid="live-feed-view-all">
                    View all
                  </Link>
                </div>
                <div className="relative flex-1 min-h-0 overflow-hidden">
                  <LiveFeedPanel
                    items={featured} activeId={activeId}
                    onEnter={setActiveId} onLeave={() => setActiveId(null)}
                    onSelect={(p) => { if (p?.story_id) navigate(`/briefs/${p.story_id}`); else if (p?.slug) navigate(`/country/${p.slug}`); }}
                  />
                  <div className="pointer-events-none absolute top-0 left-0 right-0 h-5 z-10"
                    style={{ background: "linear-gradient(to bottom, var(--paper) 0%, rgba(250,248,244,0) 100%)" }} />
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 z-10"
                    style={{ background: "linear-gradient(to top, var(--paper) 0%, rgba(250,248,244,0) 100%)" }} />
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--hairline)] shrink-0 flex items-center justify-between font-mono text-[10px] text-[var(--muted)]">
                  <span>Auto-scrolling · hover to pause</span>
                  <span>Refreshes every 15 min</span>
                </div>
              </>
            )}
          </aside>
        </div>

        {/* Stats row */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[var(--hairline)] rounded-[14px] overflow-hidden border border-[var(--hairline)]">
          {[
            { k: "Countries reporting", v: mapData?.countries_reporting ?? "…" },
            { k: "Signals · 48h",       v: mapData?.signals_48h?.toLocaleString() ?? "…" },
            { k: "Stories tracked",     v: stats?.stories_count?.toLocaleString() ?? "…" },
            { k: "Live sources",        v: stats?.sources_count ?? "…" },
          ].map((s) => (
            <div key={s.k} className="bg-[var(--paper)] p-5">
              <div className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--muted)]">{s.k}</div>
              <div className="font-serif text-[24px] sm:text-[28px] leading-tight text-[var(--ink)] mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
